import { fabric } from "fabric";
import QRCode from "easyqrcodejs";
import JsBarcode from "jsbarcode";
import {
  LOGO_IMAGE_DATA_URL,
  LOGO_IMAGE_WHITE_DATA_URL,
} from "../components/constants";
import { calculateCanvasSize } from "./calculateCanvasSize";
import { isCodeType } from "./badgeTemplate";
import { fitTextElementToCanvasWidth } from "./textElementSizing";

const normalizeCodeValue = (value) => String(value ?? "").replace(/\u00A0/g, " ");
const normalizeTextValue = (value) => String(value ?? "").replace(/ /g, "\u00A0");

export const loadFabricImage = (imageUrl, options = {}) =>
  new Promise((resolve, reject) => {
    fabric.Image.fromURL(
      imageUrl,
      (img) => {
        if (!img) {
          reject(new Error("Could not load image."));
          return;
        }

        resolve(img);
      },
      options
    );
  });

export const createCodeImageUrl = ({ type, value, imageUrl }) => {
  if (!isCodeType(type) || value == null) {
    return Promise.resolve(imageUrl || null);
  }

  if (type === "qrCode") {
    return new Promise((resolve, reject) => {
      const container = document.createElement("div");
      container.style.cssText =
        "height:0;left:-9999px;overflow:hidden;position:absolute;top:-9999px;width:0;";
      document.body.appendChild(container);

      try {
        new QRCode(container, {
          text: normalizeCodeValue(value),
          onRenderingEnd: (_options, qrCodeDataUrl) => {
            container.remove();
            resolve(qrCodeDataUrl);
          },
        });
      } catch (error) {
        container.remove();
        reject(error);
      }
    });
  }

  const barcodeCanvas = document.createElement("canvas");
  JsBarcode(barcodeCanvas, normalizeCodeValue(value), {
    format: type === "barcode39" ? "CODE39" : "CODE128",
    width: 15,
    height: 500,
    displayValue: false,
  });
  return Promise.resolve(barcodeCanvas.toDataURL());
};

const addObjectAtZIndex = (canvas, object, zIndex) => {
  canvas.add(object);
  if (zIndex !== undefined) {
    canvas.moveTo(object, zIndex);
  }
};

const scaleImageToTemplateSize = (img, element) => {
  if (element.width && element.height) {
    img.set({
      scaleX: element.width / img.width,
      scaleY: element.height / img.height,
    });
    return;
  }

  if (element.width) {
    img.scaleToWidth(element.width);
    return;
  }

  if (element.height) {
    img.scaleToHeight(element.height);
  }
};

export const renderBadgeTemplateObject = async (
  canvas,
  object,
  {
    alignCenteredText,
    getImageAssetPath,
    onLogoElement,
  } = {}
) => {
  if (!object || object.skipTemplateSave) {
    return null;
  }

  if (object.type === "rect") {
    const fabricObj = new fabric.Rect(object);
    addObjectAtZIndex(canvas, fabricObj, object.zIndex);
    return fabricObj;
  }

  if (object.type === "text" || object.type === "textbox") {
    const fabricObj = new fabric.Textbox(normalizeTextValue(object.text), object);
    if (object.lineHeight !== undefined) {
      fabricObj.set({ lineHeight: object.lineHeight });
    }

    addObjectAtZIndex(canvas, fabricObj, object.zIndex);
    if (object.textAlign === "center") {
      alignCenteredText?.(fabricObj);
    }
    return fabricObj;
  }

  if (object.type === "logo") {
    const img = await loadFabricImage(
      object.isWhiteLogo ? LOGO_IMAGE_WHITE_DATA_URL : LOGO_IMAGE_DATA_URL
    );
    img.set({
      top: object.top,
      left: object.left,
      angle: object.angle,
      isLogo: true,
    });
    if (object.width) {
      img.scaleToWidth(object.width);
    }

    onLogoElement?.(img);
    addObjectAtZIndex(canvas, img, object.zIndex);
    return img;
  }

  if (object.type === "image" || isCodeType(object.type)) {
    const imageUrl = await createCodeImageUrl(object);
    if (!imageUrl) {
      return null;
    }

    const img = await loadFabricImage(imageUrl, { crossOrigin: "anonymous" });
    img.set({
      top: object.top,
      left: object.left,
      angle: object.angle,
      imageUrl: object.imageUrl || imageUrl,
      eventAssetPath: getImageAssetPath?.(object.imageUrl || imageUrl),
    });
    if (isCodeType(object.type)) {
      img.set({
        codeType: object.type,
        value: object.value,
      });
    }

    scaleImageToTemplateSize(img, object);
    addObjectAtZIndex(canvas, img, object.zIndex);
    return img;
  }

  return null;
};

export const renderBadgeTemplateObjects = async (canvas, objects = [], options = {}) => {
  const rendered = [];
  for (const object of objects) {
    const fabricObject = await renderBadgeTemplateObject(canvas, object, options);
    if (fabricObject) {
      rendered.push(fabricObject);
    }
  }
  canvas.renderAll();
  return rendered;
};

export const renderBadgeTemplateField = async (canvas, field = {}) => {
  if (!field.enabled && field.enabled !== undefined) {
    return null;
  }

  if (!field.type || field.type === "text" || field.type === "textbox") {
    const fieldElement = new fabric.Textbox(normalizeTextValue(field.value), {
      ...field,
      id: field.label,
      editable: false,
    });
    fieldElement.scalingSetFontSize = fieldElement.fontSize;
    addObjectAtZIndex(canvas, fieldElement, field.zIndex);
    fitTextElementToCanvasWidth(canvas, fieldElement);
    return fieldElement;
  }

  if (isCodeType(field.type)) {
    const imageUrl = await createCodeImageUrl(field);
    if (!imageUrl) {
      return null;
    }

    const img = await loadFabricImage(imageUrl, { crossOrigin: "anonymous" });
    img.set({
      top: field.top,
      left: field.left,
      angle: field.angle,
      codeType: field.type,
      value: field.value,
      imageUrl,
    });
    scaleImageToTemplateSize(img, field);
    addObjectAtZIndex(canvas, img, field.zIndex);

    if (field.type === "barcode39" || field.type === "barcode128") {
      addObjectAtZIndex(
        canvas,
        new fabric.Rect({
          top: field.top + field.height - 1,
          left: field.left,
          height: 3,
          width: field.width,
          fill: "#fff",
        }),
        field.zIndex !== undefined ? field.zIndex + 1 : undefined
      );
    }

    return img;
  }

  return null;
};

export const renderBadgeTemplateToCanvas = async (
  canvas,
  {
    template,
    badgeSize,
    fields = template?.fields || [],
    objects = template?.objects || [],
    renderFields = true,
    renderObjects = true,
    clear = true,
    ...options
  }
) => {
  const canvasSize = calculateCanvasSize(badgeSize);
  if (clear) {
    canvas.clear();
  }
  canvas.setHeight(canvasSize.height);
  canvas.setWidth(canvasSize.width);

  if (renderObjects) {
    await renderBadgeTemplateObjects(canvas, objects, options);
  }

  if (renderFields) {
    await Promise.all(fields.map((field) => renderBadgeTemplateField(canvas, field)));
  }

  canvas.renderAll();
  return canvas;
};

export const renderBadgeTemplatePreview = async ({
  canvasElement,
  template,
  badgeSize,
  multiplier = 1,
}) => {
  const canvas = new fabric.Canvas(canvasElement, {
    renderOnAddRemove: false,
    selection: false,
  });

  try {
    await renderBadgeTemplateToCanvas(canvas, { template, badgeSize });
    return canvas.toDataURL({
      format: "png",
      multiplier,
    });
  } finally {
    canvas.dispose();
  }
};
